// @ts-check

/**
 * Pure helpers for the sale form's product picker. A catalogue product is
 * {product_id, name, slug, image, price, sale_price, variants: [{variant_id, size, color, color_hex, sku, stock_quantity}]}.
 * A line's choice is (size, color); the variant is settled only once that pins down exactly one.
 */

const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

/** Garment sizes first, in size order; anything else after them in natural (numeric-aware) order. */
const compareSizes = (a, b) => {
  const ia = SIZE_ORDER.indexOf(String(a).toUpperCase());
  const ib = SIZE_ORDER.indexOf(String(b).toUpperCase());
  if (ia >= 0 && ib >= 0) return ia - ib;
  if (ia >= 0) return -1;
  if (ib >= 0) return 1;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const unique = (values) => [...new Set(values.filter((x) => x != null && x !== ''))];

/** @param {{variants: any[]}} product */
export const sizeOptions = (product) => unique(product.variants.map((v) => v.size)).sort(compareSizes);

/** Colors offered, narrowed to one size when given. Kept in the order the variants come in. */
export const colorOptions = (product, size) =>
  unique(product.variants.filter((v) => size == null || v.size === size).map((v) => v.color));

const matching = (product, size, color) =>
  product.variants.filter((v) => (size == null || v.size === size) && (color == null || v.color === color));

/** The variant the (size, color) choice pins down, or null while it is still ambiguous. */
export const resolveVariant = (product, size, color) => {
  const found = matching(product, size, color);
  return found.length === 1 ? found[0] : null;
};

/** True when something matching the choice can actually be sold. */
export const hasStock = (product, { size = null, color = null }) =>
  matching(product, size, color).some((v) => v.stock_quantity > 0);

/**
 * What to preselect for a newly picked product: only what is not a real choice. With several sizes
 * or colors to pick from, nothing is preselected, so a piece is never sold in the wrong one by default.
 */
export const autoSelect = (product) => {
  const sizes = sizeOptions(product);
  const size = sizes.length === 1 ? sizes[0] : null;
  const colors = colorOptions(product, size);
  return { size, color: colors.length === 1 ? colors[0] : null };
};

/** Color to carry over when the size changes: kept if the new size has it, the only one if just one, else none. */
export const nextColorForSize = (product, size, color) => {
  const colors = colorOptions(product, size);
  if (color && colors.includes(color)) return color;
  return colors.length === 1 ? colors[0] : null;
};

/** The variant a line resolves to, shaped for the sale payload and receipt, or null. */
export const lineVariant = ({ product, size, color }) => {
  const variant = resolveVariant(product, size, color);
  if (!variant) return null;
  return {
    ...variant,
    product_id: product.product_id,
    product_name: product.name,
    price: product.price,
    sale_price: product.sale_price,
  };
};

/** The product a typed/selected name matches, or null - free text, or a name no longer in the catalogue. */
export const productByName = (products, name) => (name ? products.find((p) => p.name === name) ?? null : null);

/** Products whose name or any variant SKU contains the text. Empty text means every product. */
export const filterProducts = (products, query) => {
  const text = query.trim().toLowerCase();
  if (!text) return products;
  return products.filter(
    (p) => p.name.toLowerCase().includes(text) || p.variants.some((v) => (v.sku || '').toLowerCase().includes(text)),
  );
};
